import {
  Activity,
  BarChart3,
  BookOpen,
  BriefcaseBusiness,
  Bug,
  Building2,
  Calculator,
  Calendar,
  ChartNoAxesColumn,
  DatabaseZap,
  FileSignature,
  FileText,
  Files,
  FlaskConical,
  FolderKanban,
  FolderOpen,
  Gauge,
  GitCompareArrows,
  Globe,
  Home,
  Kanban,
  Key,
  Landmark,
  Layers,
  LayoutDashboard,
  Mail,
  MessageSquare,
  MessageSquareMore,
  Radio,
  Scale,
  Search,
  Send,
  Shield,
  ShieldCheck,
  Sparkles,
  Timer,
  TrendingUp,
  UserCog,
  Users,
  Zap,
} from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SidebarMenuItem } from './sidebar-types';
import { hasRoleAccess } from '@/lib/role-access';
import { useAuthStore } from '@/stores/auth-store';

type MenuRole = 'ADMIN' | 'MANAGER' | 'OFFICER' | 'COLLECTOR' | 'VIEWER';

function matchRole(itemRoles: MenuRole[] | undefined, role?: string) {
  return !itemRoles || hasRoleAccess(role, itemRoles);
}

function item(
  label: string,
  pathname: string,
  icon: SidebarMenuItem['icon'],
  navigate: (path: string) => void,
  keywords: string[] = [],
  roles?: MenuRole[],
): SidebarMenuItem {
  return {
    label,
    pathname,
    icon,
    activeIcon: icon,
    roles,
    searchKeywords: [label.toLowerCase(), ...keywords],
    onClick: () => navigate(pathname),
    opener: true,
  };
}

export function useSidebarOptions(): SidebarMenuItem[] {
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const role = user?.role;

  return useMemo(() => {
    const section = (
      label: string,
      pathname: string,
      icon: SidebarMenuItem['icon'],
      subItems: SidebarMenuItem[],
      roles?: MenuRole[],
      defaultOpen = false,
      opener = true,
    ): SidebarMenuItem => ({
      label,
      pathname,
      icon,
      activeIcon: icon,
      roles,
      opener,
      defaultOpen,
      searchKeywords: [label.toLowerCase()],
      onClick: pathname ? () => navigate(pathname) : undefined,
      subItems,
    });

    const topLevel: SidebarMenuItem[] = [
      section(
        'Home',
        '',
        Home,
        [
          item('Dashboard', '/dashboard', LayoutDashboard, navigate, [
            'overview',
            'summary',
            'stats',
          ]),
          item('My Work', '/diary', Calendar, navigate, ['diary', 'tasks', 'schedule', 'calendar']),
          item(
            'KPI Dashboard',
            '/admin/kpi-dashboard',
            Gauge,
            navigate,
            ['kpi'],
            ['ADMIN', 'MANAGER'],
          ),
          item(
            'My Widgets',
            '/admin/custom-dashboard',
            LayoutDashboard,
            navigate,
            ['widgets', 'custom dashboard'],
            ['ADMIN', 'MANAGER'],
          ),
        ].filter(menuItem => matchRole(menuItem.roles as MenuRole[], role)),
        undefined,
        true,
      ),
      section(
        'Loans',
        '',
        FileText,
        [
          item('All Loans', '/loans', FolderOpen, navigate, ['loans', 'list']),
          item('Board', '/loans/board', Kanban, navigate, ['kanban']),
          item('Compare', '/loans/compare', GitCompareArrows, navigate, ['compare']),
          item('Search', '/search', Search, navigate, ['advanced search']),
        ],
        undefined,
        false,
      ),
      section(
        'Origination',
        '',
        Layers,
        [
          item('Pipeline', '/pipeline', Globe, navigate, [
            'application pipeline',
            'origination funnel',
            'workflow',
          ]),
          item('New Application', '/origination', Layers, navigate, [
            'origination',
            'wizard',
            'application',
          ]),
          item('Borrowers', '/borrowers', Users, navigate, ['clients', 'people', 'parties']),
          item(
            'Lenders',
            '/admin/lenders',
            Building2,
            navigate,
            ['funders', 'parties'],
            ['ADMIN', 'MANAGER'],
          ),
        ].filter(menuItem => matchRole(menuItem.roles as MenuRole[], role)),
        undefined,
        false,
      ),
      section(
        'Servicing',
        '',
        BriefcaseBusiness,
        [
          item(
            'Collections',
            '/collections',
            Shield,
            navigate,
            ['overdue'],
            ['ADMIN', 'MANAGER', 'COLLECTOR'],
          ),
          item(
            'Bulk Operations',
            '/bulk-operations',
            Layers,
            navigate,
            ['month-end', 'accruals', 'maturity notices'],
            ['ADMIN', 'MANAGER'],
          ),
          item('Calculator', '/calculator', Calculator, navigate, ['loan', 'amortisation', 'lvr']),
          item(
            'Loan Terms',
            '/admin/loan-terms',
            Timer,
            navigate,
            ['terms', 'policy'],
            ['ADMIN', 'MANAGER', 'OFFICER'],
          ),
        ].filter(menuItem => matchRole(menuItem.roles as MenuRole[], role)),
        undefined,
        false,
      ),
      section(
        'Finance & Treasury',
        '',
        Landmark,
        [
          item(
            'Portfolio',
            '/portfolio',
            TrendingUp,
            navigate,
            ['assets'],
            ['ADMIN', 'MANAGER', 'OFFICER'],
          ),
          item('Analytics', '/analytics', BarChart3, navigate, [], ['ADMIN', 'MANAGER', 'OFFICER']),
          item('Treasury', '/treasury', Landmark, navigate, [], ['ADMIN', 'MANAGER']),
          item(
            'Funding Lines',
            '/treasury/funding-lines',
            Building2,
            navigate,
            [],
            ['ADMIN', 'MANAGER'],
          ),
          item(
            'Benchmarks',
            '/benchmarks',
            TrendingUp,
            navigate,
            ['rates', 'rba', 'bbsw'],
            ['ADMIN', 'MANAGER', 'OFFICER'],
          ),
        ],
        undefined,
        false,
      ),
      section(
        'Reports',
        '',
        BookOpen,
        [
          item(
            'Reports',
            '/reports',
            Files,
            navigate,
            ['overview', 'pipeline', 'risk', 'performance'],
            ['ADMIN', 'MANAGER', 'OFFICER'],
          ),
          item(
            'Build / Saved',
            '/report-builder',
            ChartNoAxesColumn,
            navigate,
            ['report builder', 'ad-hoc', 'saved reports', 'definitions', 'report engine'],
            ['ADMIN', 'MANAGER', 'OFFICER'],
          ),
          item('Board Packs', '/board-packs', FolderKanban, navigate, [], ['ADMIN', 'MANAGER']),
          item(
            'Data Exports',
            '/data-exports',
            Send,
            navigate,
            ['exports', 'executive summaries'],
            ['ADMIN', 'MANAGER'],
          ),
        ].filter(menuItem => matchRole(menuItem.roles as MenuRole[], role)),
        undefined,
        false,
      ),
      section(
        'Compliance & Risk',
        '',
        Scale,
        [
          item(
            'Risk',
            '/risk',
            ShieldCheck,
            navigate,
            ['portfolio risk'],
            ['ADMIN', 'MANAGER', 'OFFICER'],
          ),
          item(
            'Compliance',
            '/compliance',
            FileText,
            navigate,
            ['checks', 'breaches', 'afsl', 'capital adequacy'],
            ['ADMIN', 'MANAGER'],
          ),
          item(
            'Regulatory Returns',
            '/regulatory-returns',
            FileSignature,
            navigate,
            ['asic', 'afsl returns'],
            ['ADMIN', 'MANAGER'],
          ),
          item(
            'Trust Accounts',
            '/trust-accounts',
            Landmark,
            navigate,
            ['reconciliation', 'regulated money'],
            ['ADMIN', 'MANAGER'],
          ),
          item(
            'Alerts',
            '/alerts',
            Shield,
            navigate,
            ['rules', 'scheduled compliance reports'],
            ['ADMIN', 'MANAGER'],
          ),
        ].filter(menuItem => matchRole(menuItem.roles as MenuRole[], role)),
        undefined,
        false,
      ),
      section(
        'Communications',
        '',
        Radio,
        [
          item('Email', '/email', Mail, navigate, ['email center']),
          item(
            'SMS Center',
            '/admin/sms-center',
            MessageSquareMore,
            navigate,
            [],
            ['ADMIN', 'MANAGER'],
          ),
          item(
            'Bulk Comms',
            '/admin/bulk-comms',
            Send,
            navigate,
            ['campaigns'],
            ['ADMIN', 'MANAGER'],
          ),
        ].filter(menuItem => matchRole(menuItem.roles as MenuRole[], role)),
        undefined,
        false,
      ),
      section(
        'Automation',
        '',
        Zap,
        [
          item(
            'Automation Rules',
            '/automation',
            Zap,
            navigate,
            ['rule engine', 'automation', 'workflow rules'],
            ['ADMIN', 'MANAGER'],
          ),
          item(
            'Document Templates',
            '/templates',
            FileText,
            navigate,
            ['templates', 'doc templates'],
            ['ADMIN', 'MANAGER'],
          ),
          item(
            'E-Signatures',
            '/admin/e-signature',
            MessageSquareMore,
            navigate,
            ['signatures'],
            ['ADMIN', 'MANAGER', 'OFFICER'],
          ),
        ].filter(menuItem => matchRole(menuItem.roles as MenuRole[], role)),
        undefined,
        false,
      ),
      {
        label: 'Administration',
        pathname: '',
        icon: UserCog,
        activeIcon: UserCog,
        opener: true,
        defaultOpen: false,
        searchKeywords: ['administration'],
        subSections: [
          {
            label: 'Access',
            items: [
              item('Users', '/admin/users', UserCog, navigate, ['user management'], ['ADMIN']),
              item(
                'Authority',
                '/admin/authority',
                Shield,
                navigate,
                ['delegated authority'],
                ['ADMIN', 'MANAGER'],
              ),
              item(
                'Borrower Portal',
                '/admin/borrower-portal',
                Users,
                navigate,
                ['portal'],
                ['ADMIN', 'MANAGER'],
              ),
            ],
          },
          {
            label: 'Integrations & Data',
            items: [
              item(
                'API Portal',
                '/admin/api-portal',
                Key,
                navigate,
                ['keys', 'usage', 'docs'],
                ['ADMIN'],
              ),
              item('Webhooks', '/admin/webhooks', Globe, navigate, ['event endpoints'], ['ADMIN']),
              item(
                'Import',
                '/import',
                DatabaseZap,
                navigate,
                ['data import'],
                ['ADMIN', 'MANAGER'],
              ),
              item(
                'Data Migration',
                '/admin/data-migration',
                DatabaseZap,
                navigate,
                ['migration'],
                ['ADMIN'],
              ),
            ],
          },
          {
            label: 'Platform & Audit',
            items: [
              item(
                'Audit Log',
                '/admin/audit',
                ShieldCheck,
                navigate,
                ['security', 'audit'],
                ['ADMIN'],
              ),
              item('Activity', '/activity', Activity, navigate, ['system activity']),
              item(
                'Field Audit',
                '/admin/field-audit',
                Activity,
                navigate,
                ['field changes'],
                ['ADMIN', 'MANAGER'],
              ),
              item(
                'Chat / AI Logs',
                '/chat-history',
                MessageSquare,
                navigate,
                ['chat logs', 'ai logs', 'personal chat history'],
                ['ADMIN', 'MANAGER', 'OFFICER', 'COLLECTOR', 'VIEWER'],
              ),
              item(
                'Architecture',
                '/admin/architecture',
                LayoutDashboard,
                navigate,
                ['platform reference'],
                ['ADMIN'],
              ),
            ],
          },
          {
            label: 'Product & Quality',
            items: [
              item('Feature Requests', '/features', Sparkles, navigate, ['roadmap', 'requests']),
              item('Bug Tracker', '/bugs', Bug, navigate, ['bugs', 'quality']),
              item(
                'QA Tracker',
                '/admin/qa-tracker',
                FlaskConical,
                navigate,
                ['qa', 'quality'],
                ['ADMIN', 'MANAGER'],
              ),
            ],
          },
        ]
          .map(sectionGroup => ({
            ...sectionGroup,
            items: sectionGroup.items.filter(menuItem =>
              matchRole(menuItem.roles as MenuRole[], role),
            ),
          }))
          .filter(sectionGroup => sectionGroup.items.length > 0),
      },
    ];

    return topLevel
      .map(sectionItem => ({
        ...sectionItem,
        subItems: sectionItem.subItems?.filter(subItem =>
          matchRole(subItem.roles as MenuRole[], role),
        ),
      }))
      .filter(sectionItem => !sectionItem.roles || matchRole(sectionItem.roles as MenuRole[], role))
      .filter(sectionItem => {
        if (sectionItem.subItems) return sectionItem.subItems.length > 0;
        if (sectionItem.subSections) return sectionItem.subSections.length > 0;
        return true;
      });
  }, [navigate, role]);
}
